import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useTypedSelector } from '@redux/Hooks/storeSelectors';
import {
    selectCurrentProjectId,
    selectPresetsById,
    selectPresetType,
    selectProjectPresets,
    selectProjectRuns,
    selectRunsById,
} from '@redux/selectors/selectors';
import { isErrorResponse } from '@utils/utils';
import { updateSearchParam } from '@utils/storageUtils';
import { addedPresets, addedRuns, ProjectPreset } from '@redux/reducers/projectsReducer';
import { PublicTransitPresetEntity } from '@api/entities/replancity_PublicTransitPreset';
import { presetsApi } from '@api/presetsApi/presetsApi';
import { ApiError } from '@api/RestClient';
import {
    CopyPresetFnArgs,
    CreationPreset,
    PRESET_BY_ENTITY_NAME_KEY,
    PresetEntityName,
    Run,
    RunEntityName,
} from '@api/presetsApi/types';
import { entityApi } from '@api/entityApi';
import { RunTaskEntity } from '@api/entities/replancity_RunTask';
import { runApi } from '@api/runApi';
import { SimulationState } from '@api/entities/replancity_RunnedAlgorithm';
import type * as schema from '../shared/api/schema';
import { useNavigatePreserveSearch } from '@hooks/useNavigatePreserveSearch';

export type AddPresetForm = {
    serviceName: string;
    data: {
        sourcePresetId?: string;
        presetName: string;
    };
};

type ContextType = {
    runsLoading: boolean;
    presetsLoading: boolean;
    presetsLoaded: boolean;
    loadRuns: (
        selectedPresetEntityName?: PresetEntityName,
        additionalRuns?: schema.definitions['entities_replancity_RunTask'][]
    ) => Promise<void>;
    getRunParams: (runId: string) => Promise<RunTaskEntity>;
    pinRun: (runId: string, isPinned: boolean) => Promise<void>;
    loadPresetTypes: () => Promise<CreationPreset[]>;
    loadPresets: (presetEntityName: PresetEntityName) => Promise<void>;
    loadProjectPresets: () => Promise<unknown>;
    copyPresetDistrictsToPreset: (
        args: schema.paths['/services/replancity_RoadInfrastructureService/copyDistricts']['get']['parameters']['query']
    ) => Promise<any>;
    runs: schema.definitions['entities_replancity_RunTask'][];
    runsById: Record<string, schema.definitions['entities_replancity_RunTask']>;
    runsWithoutAdditional: schema.definitions['entities_replancity_RunTask'][];
    presets: ProjectPreset[];
    presetsById: Record<string, ProjectPreset>;
    selectedRunId: string;
    selectedPresetId: string;
    selectedPresetEntityName: PresetEntityName;
    addPreset: (
        presetEntityName: PresetEntityName,
        args: AddPresetForm
    ) => Promise<PublicTransitPresetEntity | ApiError>;
    selectPreset: (id: string) => void;
    selectRun: (id: string) => void;
    cleanSelectedPreset: () => void;
    cleanPresets: () => void;
    cleanSelectedRun: () => void;
    cleanRuns: () => void;
};

const PresetsContext = createContext<ContextType>({} as ContextType);

const usePresetsContext = () => {
    return useContext(PresetsContext);
};

const PresetsProvider = ({ children }) => {
    const [presetsLoading, setPresetsLoading] = useState<boolean>(false);
    const [presetsLoaded, setPresetsLoaded] = useState<boolean>(false);
    const [runsLoaded, setRunsLoaded] = useState<boolean>(false);
    const [runsLoading, setRunsLoading] = useState<boolean>(false);
    const projectId = useTypedSelector(selectCurrentProjectId);
    const [additionalRuns, setAdditionalRuns] = useState<
        schema.definitions['entities_replancity_RunTask'][]
    >([]);
    const allRuns = useTypedSelector(selectProjectRuns);
    const allRunsById = useTypedSelector(selectRunsById);
    const [runsWithoutAdditional, setRunsWithoutAdditional] = useState<
        schema.definitions['entities_replancity_RunTask'][]
    >([]);
    const presets = useTypedSelector(selectProjectPresets);
    const presetsById = useTypedSelector(selectPresetsById);
    const selectedPresetEntityName = useTypedSelector(selectPresetType);
    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useDispatch();
    const abortControllerRef = useRef(new AbortController());

    useEffect(() => {
        return () => {
            cleanPresets();
            cleanRuns();
        };
    }, []);

    const runIdQueryParam = searchParams.get('runId');
    const presetIdQueryParam = searchParams.get('presetId');

    const isRunRelatedToPreset = useCallback(
        (run: Run, selectedPresetId: string, selectedPresetEntityName: PresetEntityName) => {
            const presetKey = PRESET_BY_ENTITY_NAME_KEY[selectedPresetEntityName];
            return run[presetKey]?.id === selectedPresetId;
        },
        []
    );

    /*
    It's required to show only preset runs if presetIdQueryParam exists
    presetIdQueryParam === null for example on project page. There isn't presets selector => all runs should be presented in a runs selector
    */
    const filteredRuns = useMemo(() => {
        return allRuns.filter(run =>
            presetIdQueryParam
                ? isRunRelatedToPreset(run, presetIdQueryParam, selectedPresetEntityName)
                : true
        );
    }, [allRuns, presetIdQueryParam, selectedPresetEntityName, isRunRelatedToPreset]);

    const filteredRunsById = useMemo(() => {
        return filteredRuns.reduce((acc, run) => {
            if (!acc[run.id]) {
                acc[run.id] = run;
            }
            return acc;
        }, {});
    }, [filteredRuns]);

    // -----------
    const cleanSelectedPreset = useCallback(() => {
        setSearchParams(prev =>
            updateSearchParam({ prevSearchParams: prev, paramsToDelete: 'presetId' })
        );
    }, [setSearchParams, updateSearchParam]);

    const cleanSelectedRun = useCallback(() => {
        setSearchParams(prev =>
            updateSearchParam({ prevSearchParams: prev, paramsToDelete: 'runId' })
        );
    }, [setSearchParams, updateSearchParam]);

    // -----------
    const cleanPresets = useCallback(() => {
        dispatch(addedPresets());
        setSearchParams(prev =>
            updateSearchParam({
                prevSearchParams: prev,
                paramsToDelete: ['presetId', 'runId'],
            })
        );
        setPresetsLoaded(false);
    }, [cleanSelectedPreset, setSearchParams, updateSearchParam]);

    const cleanRuns = useCallback(() => {
        cleanSelectedRun();
        dispatch(addedRuns());
        setSearchParams(prev =>
            updateSearchParam({ prevSearchParams: prev, paramsToDelete: 'runId' })
        );
    }, [cleanSelectedRun, setSearchParams, updateSearchParam]);

    //-------------
    const loadRuns = useCallback(
        async (
            selectedPresetEntityName?: PresetEntityName,
            additionalRuns?: schema.definitions['entities_replancity_RunTask'][]
        ) => {
            setRunsLoading(true);
            setRunsLoaded(false);

            if (additionalRuns?.length) {
                setAdditionalRuns(additionalRuns);
            }

            const resp = await runApi.getRuns({
                projectId,
                abortSignal: abortControllerRef.current.signal,
            });

            if (!isErrorResponse(resp)) {
                const ids: string[] = [];
                const presetKey = selectedPresetEntityName
                    ? PRESET_BY_ENTITY_NAME_KEY[selectedPresetEntityName]
                    : '';

                const runs = resp.filter(run => (presetKey ? run[presetKey] : true));
                setRunsWithoutAdditional(runs);

                const byId: Record<string, Run> = (additionalRuns ?? [])
                    .concat(runs)
                    .reduce((acc, { id, name, state, isPinned, ...rest }) => {
                        acc[id!] = {
                            id,
                            name,
                            state,
                            isPinned,
                            ...rest,
                        };

                        ids.push(id!);

                        return acc;
                    }, {}) as Record<string, Run>;

                dispatch(addedRuns({ byId, ids }));
            }

            setRunsLoading(false);
            setRunsLoaded(true);
        },
        [projectId]
    );

    const getRunParams = useCallback(async (runId: string): Promise<RunTaskEntity> => {
        return await runApi.getRunParams({
            runId,
            abortSignal: abortControllerRef.current.signal,
        });
    }, []);

    const loadPresets = useCallback(
        async (presetEntityName: PresetEntityName) => {
            setPresetsLoading(true);
            setPresetsLoaded(false);

            const resp = await presetsApi.getPresetsByEntityName({
                projectId,
                presetEntityName,
                abortSignal: abortControllerRef.current.signal,
            });

            if (!isErrorResponse(resp)) {
                const ids: string[] = [];
                const byId: Record<string, ProjectPreset> = resp.reduce(
                    (acc, { id, name, defaultPreset }) => {
                        acc[id!] = {
                            id: id!,
                            title: name!,
                            defaultPreset,
                        };

                        ids.push(id!);

                        return acc;
                    },
                    {} as Record<string, ProjectPreset>
                );

                dispatch(addedPresets({ byId, ids, presetType: presetEntityName }));
            }

            setPresetsLoading(false);
            setPresetsLoaded(true);
        },
        [projectId]
    );

    const loadProjectPresets = useCallback(async () => {
        const abortController = new AbortController();
        const resp = await presetsApi.getProjectPresets({
            projectId,
            abortSignal: abortController.signal,
        });
        return resp;
    }, [projectId]);

    const loadPresetTypes = useCallback(async () => {
        const abortController = new AbortController();
        return await presetsApi.getPresetsTypes({ abortSignal: abortController.signal });
    }, [projectId]);

    const copyPresetDistrictsToPreset = useCallback(
        async (
            args: schema.paths['/services/replancity_RoadInfrastructureService/copyDistricts']['get']['parameters']['query']
        ) => {
            const abortController = new AbortController();
            const resp = await presetsApi.copyPresetDistrictsToPreset({
                ...args,
                abortSignal: abortController.signal,
            });
            return resp;
        },
        []
    );

    //------------
    const pinRun = useCallback(
        async (runId: string, isPinned: boolean) => {
            setRunsLoading(true);

            const abortController = new AbortController();
            const resp = await entityApi.saveEntity<
                schema.definitions['entities_replancity_RunTask']
            >({
                entityName: RunEntityName,
                data: {
                    id: runId,
                    isPinned,
                },
                abortSignal: abortController.signal,
            });

            await loadRuns(selectedPresetEntityName, additionalRuns);

            setRunsLoading(false);
        },
        [loadRuns, selectedPresetEntityName, additionalRuns]
    );
    // -----------
    const selectPreset = useCallback(
        (id: string) => {
            setSearchParams(prev =>
                updateSearchParam({
                    prevSearchParams: prev,
                    paramsToSet: { presetId: id },
                })
            );
        },
        [setSearchParams, updateSearchParam]
    );

    const selectRun = useCallback(
        (id: string) => {
            setSearchParams(prev =>
                updateSearchParam({ prevSearchParams: prev, paramsToSet: { runId: id } })
            );
        },
        [setSearchParams, updateSearchParam]
    );

    // -----------
    const addPreset = useCallback(
        async (
            presetEntityName: PresetEntityName,
            args: AddPresetForm
        ): Promise<PublicTransitPresetEntity | ApiError> => {
            setPresetsLoading(true);

            let resp;
            if (args.data['sourcePresetId'] && args['serviceName']) {
                resp = await presetsApi.copyPreset({
                    ...args,
                    abortSignal: abortControllerRef.current.signal,
                } as CopyPresetFnArgs);
            } else {
                const { presetName, ...restData } = args.data;

                resp = await entityApi.saveEntity<typeof presetEntityName>({
                    entityName: presetEntityName,
                    data: {
                        project: {
                            id: projectId,
                        },
                        ...restData,
                        name: presetName,
                    },
                    abortSignal: abortControllerRef.current.signal,
                });
            }

            if (!isErrorResponse(resp) && presetEntityName === selectedPresetEntityName) {
                await loadPresets(presetEntityName);

                const { id } = resp;
                selectPreset(id);
            }
            setPresetsLoading(false);

            return resp;
        },
        [projectId, selectedPresetEntityName]
    );

    // -----------
    useEffect(() => {
        if (!presets.length && presetsLoaded) {
            if (presetIdQueryParam) {
                cleanSelectedPreset();
            }
            return;
        }

        if (!presetsLoaded) {
            return;
        }

        if (presetIdQueryParam && presetsById?.[presetIdQueryParam]) {
            return;
        }

        const defaultPresetId = presets.find(({ defaultPreset }) => !!defaultPreset)?.id;
        if (defaultPresetId) {
            selectPreset(defaultPresetId);
            return;
        } else {
            selectPreset(presets[0].id);
            return;
        }
    }, [presetsLoaded, presets, presetsById, presetIdQueryParam, selectPreset]);

    useEffect(() => {
        if (!filteredRuns.length && runsLoaded && runIdQueryParam) {
            cleanSelectedRun();
            return;
        }

        if (!runsLoaded) {
            return;
        }

        if (runIdQueryParam && filteredRunsById?.[runIdQueryParam]) {
            return;
        }

        if (filteredRuns.length) {
            selectRun(filteredRuns[0].id!);
            return;
        }
    }, [runsLoaded, filteredRuns, filteredRunsById, runIdQueryParam, selectRun, cleanSelectedRun]);

    const value = {
        presetsLoading,
        presetsLoaded,
        runsLoading,
        loadRuns,
        getRunParams,
        pinRun,
        loadPresetTypes,
        loadPresets,
        loadProjectPresets,
        copyPresetDistrictsToPreset,
        runs: filteredRuns,
        runsById: filteredRunsById,
        runsWithoutAdditional,
        presets,
        presetsById,
        selectedRunId: runsLoaded && !runsLoading ? (runIdQueryParam ?? '') : '',
        selectedPresetId: presetsLoaded && !presetsLoading ? (presetIdQueryParam ?? '') : '',
        selectedPresetEntityName,
        addPreset,
        selectPreset,
        selectRun,
        cleanSelectedPreset,
        cleanPresets,
        cleanSelectedRun,
        cleanRuns,
    };

    return <PresetsContext.Provider value={value}>{children}</PresetsContext.Provider>;
};

export { PresetsProvider, usePresetsContext };
